/**
 * The page-level states every Focus screen shares, in one fixed order
 * (`FocusGate`), plus the empty state for dates or filters the data does not
 * cover (`CoverageEmptyState`) and the bar's own skeleton.
 */
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { hasNoDataOfItsOwn, type AvailabilityState } from './api';
import type { FocusConfig } from './config';
import { formatDateChip, periodOfWindow, scopeCoverage, trailingWindow } from './dates';
import type { FocusSearch } from './search';
import type { FocusAppScope, FocusModel, FocusScope } from './useFocus';
import { recommendedWindow } from './window';
import { SCREENS } from '@/app/screens';
import { getErrorMessage } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { InlineError } from '@/components/ui/inline-error';
import { Notice, NoticeList } from '@/components/ui/notice';
import { Skeleton } from '@/components/ui/skeleton';
import { ExploreSampleButton } from '@/features/sample/SampleBanner';
import { SAMPLE_GAME } from '@/lib/sample';
import { pageAttrs, type PageState } from '@/lib/tour';

/**
 * Account-wide reasons there is nothing to show, from the API (never inferred
 * from an empty report). Wording and destinations are apps/web's
 * (utils/performanceAvailability.ts), checked in this order (parity §2).
 */
type Destination = (typeof SCREENS)['noConnections' | 'connections' | 'billing']['path'];

type AccountState = Exclude<AvailabilityState, 'available'>;

const ACCOUNT_STATES: Record<AccountState, { message: string; label: string; to: Destination }> = {
  no_connections: { message: 'Connect an ad network to see performance data.', label: 'Connect ad networks', to: SCREENS.noConnections.path },
  no_enabled_accounts: { message: 'No ad accounts are enabled.', label: 'Review connections', to: SCREENS.connections.path },
  no_entitled_apps: { message: 'Select applications to view performance data.', label: 'Select applications', to: SCREENS.billing.path },
  no_reportable_data: { message: 'No performance data is available yet.', label: 'Review connections', to: SCREENS.connections.path },
};


export function AccountEmptyState({ state }: { state: AccountState }) {
  const { message, label, to } = ACCOUNT_STATES[state];
  if (!hasNoDataOfItsOwn(state)) {
    return (
      <div className="mx-gutter mt-6 rounded-card border border-line bg-surface">
        <EmptyState message={message} action={{ label, to }} className="py-16" />
      </div>
    );
  }
  return (
    <section aria-label="Get started" className="mx-gutter mt-6 flex flex-col items-center gap-4 rounded-card border border-line bg-surface px-4 py-14 text-center">
      <p className="m-0 max-w-[48ch] text-base font-semibold text-fg">{message}</p>
      <p className="m-0 max-w-[52ch] text-sm text-muted">
        {state === 'no_reportable_data' ? 'A first sync can take a few hours. ' : null}
        Meanwhile, every screen works on sample data from {SAMPLE_GAME}, a fictional game.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button asChild variant="primary">
          <Link to={to}>{label}</Link>
        </Button>
        <ExploreSampleButton />
      </div>
    </section>
  );
}

type FocusGateProps<S extends FocusSearch, Scope extends FocusAppScope> = {
  focus: FocusModel<S, Scope>;
  /** The screen's loading shape (use `FocusBarSkeleton` for its top bar). */
  skeleton: ReactNode;
  /** The screen, once its scope is resolved. */
  children: (scope: Scope) => ReactNode;
};

/**
 * Renders the first state that applies: the account has nothing to show →
 * the availability check failed → the Focus could not be read → the account
 * has no entitled app → still resolving (skeleton) → the screen.
 */
export function FocusGate<S extends FocusSearch, Scope extends FocusAppScope>({ focus, skeleton, children }: FocusGateProps<S, Scope>) {
  const { state, node } = gate(focus, skeleton, children);
  // The guided tour reads what the screen shows (src/lib/tour.ts); `contents` adds no box.
  return <div className="contents" {...pageAttrs(state)}>{node}</div>;
}

function gate<S extends FocusSearch, Scope extends FocusAppScope>(
  focus: FocusModel<S, Scope>,
  skeleton: ReactNode,
  children: (scope: Scope) => ReactNode,
): { state: PageState; node: ReactNode } {
  const { availability, accountState, resolution, scope } = focus;
  if (accountState && accountState !== 'available') return { state: 'empty', node: <AccountEmptyState state={accountState} /> };
  if (availability.isError && !availability.data) {
    return {
      state: 'error',
      node: (
        <div className="mx-gutter mt-6">
          <InlineError message="We couldn’t check data availability." onRetry={() => { void availability.refetch(); }} retrying={availability.isFetching} />
        </div>
      ),
    };
  }
  if (focus.wantsFocus && focus.focus.isError) {
    return {
      state: 'error',
      node: (
        <div className="mx-gutter mt-6">
          <InlineError
            message={`We couldn’t load your Focus app. ${getErrorMessage(focus.focus.error)}`}
            onRetry={() => { void focus.focus.refetch(); }}
            retrying={focus.focus.isFetching}
          />
        </div>
      ),
    };
  }
  if (resolution.kind === 'none') return { state: 'empty', node: <AccountEmptyState state="no_entitled_apps" /> };
  if (!scope) return { state: 'loading', node: skeleton };
  return { state: 'ready', node: children(scope) };
}

const Framed = ({ children }: { children: ReactNode }) => (
  <div className="mx-gutter mt-6 mb-8 rounded-card border border-line bg-surface" {...pageAttrs('empty')}>{children}</div>
);

type CoverageEmptyStateProps<S extends FocusSearch> = {
  focus: FocusModel<S>;
  scope: FocusScope;
  /** Open the Filters panel (the "Adjust filters" action). */
  onAdjustFilters: () => void;
};

/**
 * Nothing to show because of the chosen scope, not the account: the filters
 * match no data at all ("Adjust filters"), or the dates lie outside the data
 * ("Show latest available data": the same window, re-anchored on the last
 * data day). Renders null when the data covers the scope; the screen shows
 * its content then.
 */
export function CoverageEmptyState<S extends FocusSearch>({ focus, scope, onAdjustFilters }: CoverageEmptyStateProps<S>) {
  const bounds = focus.bounds ?? null;
  const dates = { from: scope.dateFrom, to: scope.dateTo };
  const coverage = scopeCoverage(dates, bounds);
  if (coverage === 'no_data') {
    return (
      <Framed>
        <EmptyState message="No creatives match these filters." action={{ label: 'Adjust filters', onClick: onAdjustFilters }} className="py-16" />
      </Framed>
    );
  }
  if (coverage === 'outside' && bounds?.first && bounds.last) {
    const { first, last } = bounds;
    return (
      <Framed>
        <EmptyState
          message={`No performance data for ${formatDateChip(dates.from, dates.to)}; data is available ${formatDateChip(first, last)}.`}
          action={{
            label: 'Show latest available data',
            onClick: () => {
              const days = scope.windowDays ?? recommendedWindow(scope.appIds, focus.focusApps.data?.apps);
              const range = trailingWindow(days, last, first);
              focus.actions.setDates(range.from, range.to, periodOfWindow(days));
            },
          }}
          className="py-16"
        />
      </Framed>
    );
  }
  return null;
}

/** The "your saved Focus was replaced" notice, under the bar (rendered by `FocusBar`). */
export function FocusReplacedNotice({ message, onDismiss }: { message: string | null; onDismiss: () => void }) {
  if (!message) return null;
  return (
    <NoticeList className="mx-gutter mt-4">
      <Notice
        tone="info"
        action={
          <Button size="icon-sm" variant="ghost" aria-label="Dismiss" onClick={onDismiss}>
            <X className="size-4" aria-hidden="true" />
          </Button>
        }
      >
        {message}
      </Notice>
    </NoticeList>
  );
}

/** The Focus bar at its real size while the scope resolves; `end` stands in for the screen's own controls. */
export function FocusBarSkeleton({ config, end }: { config: Pick<FocusConfig, 'dates' | 'filters'>; end?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-gutter py-band">
      <Skeleton width={168} height={34} className="rounded-ctl" />
      {config.dates ? <Skeleton width={232} height={32} className="rounded-ctl" /> : null}
      {config.filters ? <Skeleton width={84} height={32} className="rounded-ctl" /> : null}
      {end}
    </div>
  );
}
